// src/components/BuildTracker.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './BuildTracker.css';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || ''
});

const BuildTracker = ({ customerEmail, isVisible = true }) => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const dropdownRef = useRef(null);

  // State descriptions
  const stateDescriptions = {
    1: "Order Placed - We've received your order and are making preparations. If any parts need to be ordered, we'll do that first — otherwise expect your lights to be opened soon.",
    2: "Lights Opened - Your lights have been cooked open and are being prepped for paint.",
    3: "Paint Complete - Your lights have fully cured and are ready for retrofitting.",
    4: "Retrofit Complete - All components are secured & tested and headlights are sealed. Next we complete the wiring harness and flood test the seal.",
    5: "Seal Test Passed, Pending Shipment - Your headlights are almost ready! We're just packaging them up and doing a final check!",
    6: "Order Shipped - Your order is on its way! Check your tracking information and thank you for choosing DOCTORdripp!"
  };

  const stateLabels = {
    1: "Order Placed",
    2: "Lights Opened", 
    3: "Paint Complete",
    4: "Retrofit Complete",
    5: "Seal Test Passed",
    6: "Order Shipped"
  };

  // Fetch orders on component mount
  useEffect(() => {
    if (customerEmail && isVisible) {
      fetchOrders();
    }
  }, [customerEmail, isVisible]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Animate progress bar
  useEffect(() => {
    if (selectedOrder) {
      const targetProgress = getProgressPercentage(selectedOrder.current_state);
      animateProgress(targetProgress);
    }
  }, [selectedOrder]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/build-tracker?action=orders&email=${encodeURIComponent(customerEmail)}`);
      
      if (response.data.success && response.data.orders.length > 0) {
        setOrders(response.data.orders);
        // Default to most recent order
        setSelectedOrder(response.data.orders[0]);
      } else {
        setOrders([]);
        setSelectedOrder(null);
      }
    } catch (err) {
      console.error('Error fetching build tracker orders:', err);
      setError('Failed to load build tracker');
    } finally {
      setLoading(false);
    }
  };

  const animateProgress = (targetProgress) => {
    setCurrentProgress(0);
    
    const duration = 1500; // 1.5 seconds
    const steps = 60; // 60 FPS
    const increment = targetProgress / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= targetProgress) {
        current = targetProgress;
        clearInterval(timer);
      }
      setCurrentProgress(Math.round(current));
    }, duration / steps);
  };

  const getProgressPercentage = (state) => {
    if (state <= 1) return 0;
    if (state >= 6) return 100;
    return ((state - 1) / 5) * 100;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setShowDropdown(false);
  };

  // Don't render if not visible, loading, has error, or no orders
  if (!isVisible || loading || error || !orders.length || !selectedOrder) {
    return null;
  }

  return (
    <div className="build-tracker-header">
      <div className="build-tracker-container">
        
        {/* Left Section - Progress Info */}
        <div className="build-tracker-left">
          <div className="build-tracker-title">
            BUILD PROGRESS
          </div>
          <div className="build-tracker-state">
            {stateLabels[selectedOrder.current_state]}
            {selectedOrder.tracking_number && (
              <span className="tracking-info">
                • Tracking: {selectedOrder.tracking_number}
              </span>
            )}
          </div>
        </div>

        {/* Center Section - Progress Bar */}
        <div className="build-tracker-center">
          <div className="progress-container">
            <div className="progress-bar-bg">
              <div 
                className="progress-bar-fill"
                style={{ 
                  width: `${currentProgress}%`,
                  background: selectedOrder.current_state === 6 
                    ? 'linear-gradient(90deg, #00ff00, #32cd32)' 
                    : 'linear-gradient(90deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)'
                }}
              />
            </div>
            <div className="progress-text">
              {currentProgress}% Complete
            </div>
          </div>
          
          <div className="build-tracker-description">
            {stateDescriptions[selectedOrder.current_state]}
          </div>
        </div>

        {/* Right Section - Order Dropdown */}
        <div className="build-tracker-right" ref={dropdownRef}>
          <div 
            className="order-selector"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div className="selected-order">
              <div className="order-number">#{selectedOrder.order_number}</div>
              <div className="order-date">{formatDate(selectedOrder.order_date)}</div>
            </div>
            <div className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}>
              ▼
            </div>
          </div>

          {showDropdown && orders.length > 1 && (
            <div className="orders-dropdown">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className={`dropdown-item ${order.id === selectedOrder.id ? 'active' : ''}`}
                  onClick={() => handleOrderSelect(order)}
                >
                  <div className="dropdown-order-info">
                    <div className="dropdown-order-number">#{order.order_number}</div>
                    <div className="dropdown-order-date">{formatDate(order.order_date)}</div>
                  </div>
                  <div className="dropdown-order-state">
                    {stateLabels[order.current_state]}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildTracker;
